import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Send, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

interface CastResponseInputModalProps {
  onClose: () => void;
  onSubmit: (text: string) => Promise<void>;
}

export const CastResponseInputModal = ({ onClose, onSubmit }: CastResponseInputModalProps) => {
  const [mounted, setMounted] = useState(false);
  const [responseText, setResponseText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setMounted(true);
    
    // Prevent body scrolling when modal is open
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleSubmit = async () => {
    if (!responseText.trim()) return;

    setIsSubmitting(true);
    try {
      await onSubmit(responseText.trim());
      setResponseText('');
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSubmit();
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!mounted) return null;

  return createPortal(
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[99999] p-4" 
      onClick={handleBackdropClick}
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 99999
      }}
    >
      <div 
        className="bg-white rounded-xl max-w-lg w-full shadow-2xl animate-scale-in"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: '90vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <MessageCircle className="text-orange-600" size={20} />
            <h2 className="text-lg font-semibold text-gray-900">Add Your Response</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Your Response
            </label>
            <Textarea
              placeholder="Share your experience, offer help, or ask follow-up questions..."
              value={responseText}
              onChange={(e) => setResponseText(e.target.value)}
              onKeyDown={handleKeyDown}
              className="min-h-[120px] resize-none"
              disabled={isSubmitting}
              autoFocus
            />
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
            <p className="text-sm text-orange-800">
              💡 <strong>Tip:</strong> Share practical advice, ask clarifying questions, or offer resources to help solve this request!
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-2 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!responseText.trim() || isSubmitting}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Posting...
                </>
              ) : (
                <>
                  <Send size={16} className="mr-2" />
                  Post Response
                </>
              )}
            </Button>
          </div>

          <div className="text-xs text-gray-500 text-center">
            Press Esc to close • Cmd/Ctrl + Enter to submit
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};